import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { FormWrapper } from 'ksnm-common-ui/lib/Form/FormWrapper';
import { Notification } from 'ksnm-common-ui/lib/Notification';

import { DesignLayout } from '@common/DesignLayout';
import { RoutePath } from '@components/AppRouter/constants';
import { usePermission } from '@src/utils/hooks/usePermission';
import { controlApplicabilityEditStore } from '@stores/dictionaryStores/controlApplicabilityStore/controlApplicabilityEditStore';
import { controlApplicabilityStore } from '@stores/dictionaryStores/controlApplicabilityStore/controlApplicabilityStore';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';

import { NEW_ENTITY_ID } from '../../../AppRouter/hooks/constants';
import { FormWithActions } from '../../FormWithActions';
import { Form } from './Form';
import { validate } from './validateSchema';

export const ControlApplicabilityEdit = observer(() => {
  const { ctrlVersCode, orgCode } = useParams<{ ctrlVersCode: string; orgCode: string }>();
  const { push } = useHistory();
  const { selectNode } = controlApplicabilityStore;
  const { setNew, fetch, setEditMode, controlApplicability, save, isNew, isEditMode } = controlApplicabilityEditStore;

  const permission = usePermission('CmControlApplicability', controlApplicabilityEditStore);

  useEffect(() => {
    fetchReferences(['controlApplicability']);
  }, []);

  useEffect(() => {
    if (ctrlVersCode !== NEW_ENTITY_ID) {
      fetch(ctrlVersCode, orgCode);
    } else {
      setNew(true);
    }
    return () => {
      setNew(false);
      setEditMode(false);
    };
  }, [fetch, setNew, setEditMode, ctrlVersCode, orgCode]);

  const initialValues = useMemo(
    () =>
      controlApplicability
        ? {
            ...controlApplicability,
            id: `${controlApplicability.orgCode}-${controlApplicability.repGroupCode}-${controlApplicability.ctrlVersCode}`,
          }
        : {
            repGroupCode: selectNode?.repGroupCode,
            orgCode: selectNode?.orgCode,
          },
    [controlApplicability, selectNode?.orgCode, selectNode?.repGroupCode],
  );

  const handleSubmit = useCallback(
    (values) => {
      if ((isNew && !permission.CREATE) || (!isNew && !permission.UPDATE)) {
        Notification({
          message: `У вас нет прав для выполнения операции ${isNew ? 'создания' : 'обновления'} формы`,
        });
        return {};
      }

      const validation = validate(values);

      if (validation) {
        return validation;
      }

      (async () => {
        const value = await save(values);

        if (isNew && value?.ctrlVersCode && value?.orgCode) {
          push(`${RoutePath.DICTIONARY_CONTROL_APPLICABILITY}/${value.ctrlVersCode}/${value.orgCode}`);
        }
      })();

      return undefined;
    },
    [isNew, permission.CREATE, permission.UPDATE, push, save],
  );

  const goToControls = useCallback(() => push(RoutePath.DICTIONARY_CONTROL_APPLICABILITY), [push]);

  return (
    <DesignLayout title="Справочник: Применимости контролей" scrollEnable requiredHelp={isEditMode}>
      <FormWrapper initialValues={initialValues} onSubmit={handleSubmit} layout="vertical" compact>
        <FormWithActions store={controlApplicabilityEditStore} goBackLink={goToControls}>
          <Form />
        </FormWithActions>
      </FormWrapper>
    </DesignLayout>
  );
});
